import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button
} from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import { useAuthContext } from '../../../hooks/useAuthContext';
import axios from '../../../services/ApiService';
import { paths } from '../../../utils/paths';
import LoadingSpinner from '../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { WorkflowTracker } from '../../types/ProgramTypes';

function BusinessSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<WorkflowTracker[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthContext();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get('/getWorkflowTrackers');
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching workflows.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'warning';
      case 'PENDING': return 'default';
      default: return 'default';
    }
  };

  const handleSubscribe = () => {
    if (user) {
      navigate(paths.business.subscribeWorkflow, { 
        state: { userId: user.id } 
      });
    }
  };

  const handleRowClick = (assignmentId: number) => {
    navigate(paths.business.workflowView, { 
      state: { assignmentId: assignmentId } 
    });
  };

  return (
    <>
      <Helmet>
        <title>BVT Business - Subscriptions</title>
      </Helmet>
      {loading ? (
        <LoadingSpinner loading={loading} />
      ) : (
        <Card>
          <CardHeader 
            subheader="Workflow Subscriptions" 
            title="Subscriptions"
            action={
              <Button
                variant="contained"
                onClick={handleSubscribe}
              >
                Subscribe New Workflow
              </Button>
            }
          />
          <Divider />
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Workflow Name</TableCell>
                    <TableCell>Program Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned Date</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No subscriptions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscriptions.map((subscription) => (
                      <TableRow 
                        key={subscription.assignmentId}
                        onClick={() => handleRowClick(subscription.assignmentId)}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell>{subscription.workflowName}</TableCell>
                        <TableCell>{subscription.programName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={subscription.status}
                            color={getStatusColor(subscription.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(subscription.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {subscription.status === 'COMPLETED' ? 'Completed on: ' : 'Last Updated: '}
                          {new Date(subscription.updatedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          {subscription.totalCost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default BusinessSubscriptions;